import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, DataTable, EmptyState, FilterBar, Pagination, type Column, fmtBRL } from "@atlas/ui/web";
import { aplicarFiltros, motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../../lib/recorte";
import { compParaInput, inputParaComp } from "../../../lib/filtro-folhas";
import { useFiltros } from "../../../lib/use-filtros";
import { atlas } from "../../../lib/sdk";
import { downloadCsv } from "../../../lib/csv";

/** Campo de mês dentro da barra em linha — sem isto ele ocupa a faixa inteira. */
const ESTILO_MES: React.CSSProperties = {
  padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border-strong)",
  background: "var(--surface)", color: "var(--text)", fontSize: 14,
};


type Linha = {
  competencia: string;
  contratos: number;
  contratosSemDadosFinanceiros: number;
  contratosSemCronograma?: number;
  valorFinanciado: number;
  totalEmFolha: number;
  comissaoEstimada: number;
};

/** `AAAAMM` -> `MM/AAAA`; `sem_data` -> "Sem data". */
function fmtCompetencia(c: string): string {
  if (/^\d{6}$/.test(c)) return `${c.slice(4, 6)}/${c.slice(0, 4)}`;
  return c === "sem_data" ? "Sem data" : c;
}

const TITULO_NAO_CALCULAVEL =
  "Contrato legado sem valor solicitado e taxa informados: a Atlas não tem a cédula, então o principal e a comissão não são calculáveis. Entram quando o credor informar os dados do contrato.";

/**
 * Valor que depende do principal: "—" quando NENHUM contrato do recorte tem
 * principal conhecido, e o numero com "(N sem dados)" quando so' parte tem.
 *
 * Zero ali nao e' "nao houve comissao": e' "nao da' para calcular" — o legado
 * de Morada Nova nao tem numero de contrato nem taxa (16/09/2026).
 */
function ValorDoPrincipal({ valor, contratos, semDados }: { valor: number; contratos: number; semDados: number }) {
  if (contratos > 0 && semDados >= contratos) {
    return <span title={TITULO_NAO_CALCULAVEL} style={{ color: "var(--text-dim)" }}>—</span>;
  }
  return (
    <span title={semDados > 0 ? `${semDados} contrato(s) legado(s) sem dados ficam fora deste valor.` : undefined}>
      {fmtBRL(valor)}
      {semDados > 0 ? <span style={{ color: "var(--text-dim)", fontSize: 11, marginLeft: 5 }}>({semDados} sem dados)</span> : null}
    </span>
  );
}

export function BancoRelatorioFaturamento() {
  const data = useQuery({ queryKey: ["banco", "rel", "faturamento"], queryFn: () => atlas.banco.relatorioFaturamento() });

  // Percentual real vem do backend (config do convenio); fallback 2% pra
  // convenios sem valor configurado. Antes o label vinha "(2%)" hardcoded
  // mesmo quando a averbadora publicava outro percentual.
  const pctComissao = data.data?.pctComissao ?? 0.02;
  const pctLabel = `${(pctComissao * 100).toFixed(pctComissao * 100 % 1 === 0 ? 0 : 2)}%`;

  const columns: Column<Linha>[] = [
    { key: "competencia", header: "Competência (folha 1º desconto)", render: (r) => fmtCompetencia(r.competencia) },
    { key: "contratos", header: "Contratos", align: "right" },
    {
      key: "valorFinanciado", header: "Valor financiado", align: "right",
      render: (r) => <ValorDoPrincipal valor={r.valorFinanciado} contratos={r.contratos} semDados={r.contratosSemDadosFinanceiros} />,
    },
    {
      key: "totalEmFolha",
      header: <span title="Parcela × total de parcelas de todos os contratos do mês, inclusive legados. Não é o valor financiado.">Total em folha</span>,
      align: "right",
      render: (r) => (r.contratosSemCronograma ?? 0) >= r.contratos ? 'Não informado' : `${fmtBRL(r.totalEmFolha)}${(r.contratosSemCronograma ?? 0) > 0 ? ' · parcial' : ''}`,
    },
    {
      key: "comissaoEstimada", header: `Comissão estimada (${pctLabel})`, align: "right",
      render: (r) => <ValorDoPrincipal valor={r.comissaoEstimada} contratos={r.contratos} semDados={r.contratosSemDadosFinanceiros} />,
    },
  ];

  const todosMeses = data.data?.meses ?? [];
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);

  // A tela nao tinha filtro NENHUM, e competencia e' a dimensao principal dela
  // (uma linha por mes, para sempre). Fechamento financeiro e' mensal, e achar
  // o mes de marco era virar pagina.
  const [compIni, setCompIni] = useState("");
  const [compFim, setCompFim] = useState("");
  const [soComContratos, setSoComContratos] = useState(false);

  const filtros = useMemo(() => {
    const ini = inputParaComp(compIni);
    const fim = inputParaComp(compFim);
    return {
      // O intervalo e' UMA dimensao. Comparacao de string funciona porque
      // `AAAAMM` tem largura fixa — ver lib/filtro-folhas.
      competencia: ini || fim
        ? (l: Linha) => (!ini || l.competencia >= ini) && (!fim || l.competencia <= fim)
        : null,
      // Mes sem contrato averbado aparece com zeros. Util para conferir que a
      // Atlas nao esqueceu o mes; ruido quando se quer so' o que faturou.
      comContratos: soComContratos ? (l: Linha) => l.contratos > 0 : null,
    };
  }, [compIni, compFim, soComContratos]);

  const linhas = useMemo(() => aplicarFiltros(todosMeses, filtros), [todosMeses, filtros]);
  const semComContratos = useMemo(() => aplicarFiltros(todosMeses, filtros, "comContratos"), [todosMeses, filtros]);
  const mesesVazios = useMemo(() => semComContratos.filter((l) => l.contratos === 0).length, [semComContratos]);

  const filtrosUi = useFiltros(
    [
      { nome: "competencia", ligado: compIni !== "" || compFim !== "", limpar: () => { setCompIni(""); setCompFim(""); } },
      { nome: "comContratos", ligado: soComContratos, limpar: () => setSoComContratos(false) },
    ],
    setPagina,
  );

  const daPagina = recortar(linhas, pagina, porPagina);
  // TOTAIS DO RECORTE, nao da base (FR-014). Somar `todosMeses` com um intervalo
  // aplicado daria um total que a tabela nao mostra — e num relatorio de
  // faturamento isso e' a diferenca entre conferir o trimestre e conferir o ano.
  const totalFinanciado = linhas.reduce((acc, l) => acc + l.valorFinanciado, 0);
  const totalComissao = linhas.reduce((acc, l) => acc + l.comissaoEstimada, 0);
  const totalEmFolha = linhas.reduce((acc, l) => acc + l.totalEmFolha, 0);
  const totalContratos = linhas.reduce((acc, l) => acc + l.contratos, 0);
  const totalSemDados = linhas.reduce((acc, l) => acc + l.contratosSemDadosFinanceiros, 0);
  const totalSemCronograma = linhas.reduce((acc, l) => acc + (l.contratosSemCronograma ?? 0), 0);
  // Nenhum contrato do recorte com principal conhecido: o total nao e' R$ 0,00.
  const principalNaoCalculavel = totalContratos > 0 && totalSemDados >= totalContratos;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Relatórios
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Faturamento (comissão)</h1>
          {/* "Competência" aqui e' a folha do PRIMEIRO DESCONTO, nao o mes em
              que o contrato foi assinado. Um contrato averbado em agosto com
              primeiro desconto em setembro aparece em setembro — e sem dizer
              isso o banco compara este relatorio com a producao comercial dele
              e acha divergencia onde ha definicao diferente. */}
          <p style={{ color: "var(--text-muted)", marginTop: 4 }}>
            Apuração mensal por convênio ativo. Cada linha é a competência da folha do <b>primeiro desconto</b> — não o mês da contratação.
            Isto é comissão sobre contratos averbados. Não conta chamadas da API — esse uso fica no painel da averbadora, em API → Uso de API.
          </p>
          {/* O LEGADO ENTRA, e a tela diz como. A competencia dele sai do
              cronograma que a folha informou, e o principal so' existe quando o
              credor o informou — sem ele a comissao nao e' calculavel. */}
          <p style={{ color: "var(--text-muted)", marginTop: 4, fontSize: 13 }}>
            Contratos legados entram na competência em que começaram a descontar. A comissão incide só sobre o valor financiado conhecido: legado sem valor e taxa informados aparece em <b>Total em folha</b> e fica fora da comissão.
          </p>
        </div>
        {/* Exporta o RECORTE, nao a base: com um intervalo aplicado, um CSV do
            ano inteiro nao seria o que a tela mostra. */}
        <Button variant="ghost" size="sm" onClick={() => downloadCsv("faturamento.csv", linhas)}>
          ⬇ Exportar CSV
        </Button>
      </header>

      {todosMeses.length > 0 ? (
        <FilterBar
          layout="linha"
          {...filtrosUi.propsFilterBar}
          actions={
            // So' aparece se houver mes vazio para esconder — controle que nao
            // corta nada e' ruido.
            mesesVazios > 0 ? (
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-muted)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={soComContratos}
                  onChange={(e) => filtrosUi.filtrar(setSoComContratos)(e.target.checked)}
                />
                Só meses com contratos
                <span style={{ color: "var(--text-dim)" }}>({mesesVazios} sem)</span>
              </label>
            ) : null
          }
        >
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
            De
            <input type="month" value={compParaInput(compIni)} onChange={(e) => filtrosUi.filtrar(setCompIni)(e.target.value)} style={ESTILO_MES} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)" }}>
            até
            <input type="month" value={compParaInput(compFim)} onChange={(e) => filtrosUi.filtrar(setCompFim)(e.target.value)} style={ESTILO_MES} />
          </label>
        </FilterBar>
      ) : null}

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <Card
          label={filtrosUi.temFiltro ? "Total financiado · recorte" : "Total financiado"}
          valor={principalNaoCalculavel ? "—" : fmtBRL(totalFinanciado)}
          hint={principalNaoCalculavel ? TITULO_NAO_CALCULAVEL : totalSemDados > 0 ? `${totalSemDados} contrato(s) legado(s) sem dados ficam fora deste valor.` : undefined}
        />
        <Card
          label={filtrosUi.temFiltro ? "Total em folha · recorte" : "Total em folha"}
          valor={totalContratos > 0 && totalSemCronograma >= totalContratos ? 'Não informado' : fmtBRL(totalEmFolha)}
          hint={totalSemCronograma > 0 ? `Soma parcial: ${totalSemCronograma} contrato(s) com cronograma pendente ficam fora.` : 'Parcela × prazo conhecido. Não é o valor financiado.'}
        />
        <Card
          label={filtrosUi.temFiltro ? "Comissão estimada · recorte" : "Comissão estimada"}
          valor={principalNaoCalculavel ? "—" : fmtBRL(totalComissao)}
          hint={principalNaoCalculavel ? TITULO_NAO_CALCULAVEL : totalSemDados > 0 ? `${totalSemDados} contrato(s) legado(s) sem dados ficam fora da comissão.` : undefined}
          accent="success"
        />
        <Card
          label="Meses no relatório"
          valor={filtrosUi.temFiltro ? `${linhas.length} de ${todosMeses.length}` : String(linhas.length)}
        />
      </div>

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(r) => r.competencia}
        loading={data.isLoading}
        emptyState={
          data.isError
            ? "Não foi possível carregar o faturamento."
            : <EmptyState
                motivo={motivoVazio(todosMeses.length, linhas.length)}
                filtroVazio="Nenhuma competência nesse intervalo."
                semDados="Nenhuma competência com contratos ainda."
                onLimpar={filtrosUi.limparFiltros}
              />
        }
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="competências"
      />
    </div>
  );
}

function Card({ label, valor, accent, hint }: { label: string; valor: string; accent?: "success"; hint?: string }) {
  return (
    <article
      title={hint}
      style={{
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: 16,
        minWidth: 220,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent === "success" ? "var(--emerald-500)" : "var(--accent)", marginTop: 6 }}>{valor}</div>
    </article>
  );
}
